import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons } from '../../../components/ui/Icons';

interface LandingHeroProps {
  onEnterApp: () => void;
}

interface AppInfo {
  src: string;
  name: string;
  cls: string;
  style: React.CSSProperties;
  glow: string;
  color: string;
  shiftFactor: number;
}

const floatingApps: AppInfo[] = [
  { src: 'https://cdn-icons-png.flaticon.com/128/5968/5968534.png', name: 'Gmail', cls: 'orbit-1', style: { top: '26%', left: '6%' }, glow: 'rgba(234,67,53,0.15)', color: '#EA4335', shiftFactor: -20 },
  { src: 'https://cdn-icons-png.flaticon.com/128/5968/5968499.png', name: 'Calendar', cls: 'orbit-2', style: { top: '24%', right: '12%' }, glow: 'rgba(66,133,244,0.15)', color: '#4285F4', shiftFactor: 15 },
  { src: 'https://upload.wikimedia.org/wikipedia/commons/4/45/Notion_app_logo.png', name: 'Notion', cls: 'orbit-3', style: { top: '72%', left: '12%' }, glow: 'rgba(0,0,0,0.1)', color: '#000000', shiftFactor: -15 },
  { src: 'https://cdn-icons-png.flaticon.com/128/5968/5968523.png', name: 'Drive', cls: 'orbit-4', style: { top: '50%', left: '5%' }, glow: 'rgba(52,168,83,0.15)', color: '#34A853', shiftFactor: 25 },
  { src: 'https://cdn-icons-png.flaticon.com/128/281/281761.png', name: 'Sheets', cls: 'orbit-5', style: { top: '48%', right: '6%' }, glow: 'rgba(15,157,88,0.15)', color: '#0F9D58', shiftFactor: -12 },
  { src: 'https://cdn-icons-png.flaticon.com/128/3800/3800024.png', name: 'Slack', cls: 'orbit-6', style: { top: '70%', right: '14%' }, glow: 'rgba(74,21,75,0.15)', color: '#4A154B', shiftFactor: 18 },
  { src: 'https://cdn-icons-png.flaticon.com/128/5968/5968872.png', name: 'HubSpot', cls: 'orbit-7', style: { top: '20%', left: '16%' }, glow: 'rgba(255,122,89,0.15)', color: '#FF7A59', shiftFactor: -28 },
];

const LandingHero: React.FC<LandingHeroProps> = ({ onEnterApp }) => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  // Mouse positions for parallax cards and interactive spotlight
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [spotlightPos, setSpotlightPos] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const scroll = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // 1. Calculate normalized parallax coords (-1 to 1)
      const px = (e.clientX - window.innerWidth / 2) / (window.innerWidth / 2);
      const py = (e.clientY - window.innerHeight / 2) / (window.innerHeight / 2);
      setMousePos({ x: px, y: py });

      // 2. Calculate local coords for spotlight relative to container
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const sx = e.clientX - rect.left;
        const sy = e.clientY - rect.top;
        setSpotlightPos({ x: sx, y: sy });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section
      ref={containerRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative w-full bg-white overflow-hidden grain-overlay"
    >
      {/* Mesh gradient & subtle grid overlay */}
      <div className="absolute inset-0 mesh-gradient pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] z-0" style={{
        backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)',
        backgroundSize: '24px 24px',
        maskImage: 'radial-gradient(ellipse 60% 50% at 50% 50%, #000 40%, transparent 100%)',
        WebkitMaskImage: 'radial-gradient(ellipse 60% 50% at 50% 50%, #000 40%, transparent 100%)'
      }} />

      {/* Interactive spotlight cursor glow */}
      {isHovered && (
        <div
          className="absolute pointer-events-none z-0 transition-transform duration-500 ease-out opacity-50"
          style={{
            left: 0,
            top: 0,
            width: '600px',
            height: '600px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(120,119,198,0.12) 0%, transparent 70%)',
            transform: `translate3d(${spotlightPos.x - 300}px, ${spotlightPos.y - 300}px, 0)`,
            mixBlendMode: 'plus-lighter'
          }}
        />
      )}

      {/* Dotted concentric circular orbits */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
        <svg className="w-[1100px] h-[1100px] opacity-[0.16] text-black" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="26" fill="none" stroke="currentColor" strokeWidth="0.06" strokeDasharray="0.6 1.4" className="origin-center" style={{ animation: 'spin 200s linear infinite' }} />
          <circle cx="50" cy="50" r="38" fill="none" stroke="currentColor" strokeWidth="0.06" strokeDasharray="0.5 1.8" className="origin-center" style={{ animation: 'spin 260s linear infinite reverse' }} />
          <circle cx="50" cy="50" r="48" fill="none" stroke="currentColor" strokeWidth="0.06" strokeDasharray="0.4 2.2" className="origin-center" style={{ animation: 'spin 340s linear infinite' }} />
        </svg>
      </div>

      {/* Nav */}
      <nav className="fixed top-0 inset-x-0 z-50 pointer-events-none">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between pointer-events-auto">
          <div className="flex items-center gap-2">
            <Icons.Logo className="w-8 h-8 rounded-lg" />
            <span className="text-lg font-bold tracking-tight text-black">Neura</span>
          </div>

          <div className="hidden md:flex items-center gap-1 text-[13px] font-medium text-black/50 bg-white/60 backdrop-blur-2xl border border-black/[0.05] px-1.5 py-1.5 rounded-full shadow-[0_1px_8px_rgba(0,0,0,0.04)]">
            {['How it works', 'Features', 'Pricing'].map((label) => (
              <button
                key={label}
                onClick={() => scroll(label.toLowerCase().replace(/ /g, '-'))}
                className="px-4 py-1.5 rounded-full hover:bg-black/[0.04] hover:text-black transition-all duration-200"
              >
                {label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button onClick={onEnterApp} className="text-[13px] font-medium text-black/50 hover:text-black transition hidden sm:block">Log in</button>
            <button onClick={onEnterApp} className="bg-black text-white text-[13px] font-semibold px-5 py-2 rounded-full btn-primary">
              Get started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 pt-32 md:pt-40 pb-20 text-center flex flex-col items-center">
        {/* Pill badge */}
        <div
          className="animate-fade-in-up inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full border border-black/[0.06] bg-white/80 backdrop-blur-sm text-[12px] font-medium text-black/40 tracking-wide shadow-sm"
          style={{ animationDelay: '0ms', opacity: 0 }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.6)] animate-pulse" />
          Now in public beta
        </div>

        {/* Headline — editorial, oversized */}
        <h1
          className="animate-fade-in-up text-[42px] sm:text-[60px] md:text-[73px] font-semibold tracking-[-0.05em] leading-[1.08] text-black max-w-4xl"
          style={{ animationDelay: '120ms', opacity: 0 }}
        >
          AI that{' '}
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-black via-black/85 to-black/50">
            actually takes action.
          </span>
        </h1>

        {/* Sub */}
        <p
          className="animate-fade-in-up mt-6 text-[16px] md:text-[18px] text-black/45 font-medium leading-relaxed max-w-2xl"
          style={{ animationDelay: '260ms', opacity: 0 }}
        >
          One chat interface. Real execution across Gmail, Calendar, Sheets, Notion, HubSpot, Slack, and 10+ apps. Type once. Done.
        </p>

        {/* CTAs */}
        <div
          className="animate-fade-in-up flex flex-col sm:flex-row gap-3 mt-10 z-20"
          style={{ animationDelay: '400ms', opacity: 0 }}
        >
          <button
            onClick={onEnterApp}
            className="group bg-black text-white px-7 py-3.5 rounded-full text-[15px] font-semibold btn-primary flex items-center gap-2 justify-center"
          >
            Start free
            <svg className="w-4 h-4 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
          <button
            onClick={() => scroll('demo')}
            className="bg-white text-black/70 border border-black/[0.08] px-7 py-3.5 rounded-full text-[15px] font-medium hover:text-black hover:border-black/[0.15] transition-all"
          >
            See it work
          </button>
        </div>

        <p
          className="animate-fade-in-up mt-6 text-[11px] text-black/25 font-semibold tracking-wide"
          style={{ animationDelay: '550ms', opacity: 0 }}
        >
          Free forever · No credit card · Setup in 2 min
        </p>
      </div>

      {/* Orbiting App Icons (Parallax-Enhanced) */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden hidden lg:block z-10">
        <div className="relative max-w-[1400px] mx-auto h-full">
          {floatingApps.map((app, i) => {
            const parallaxStyle = {
              transform: `translate3d(${mousePos.x * app.shiftFactor}px, ${mousePos.y * app.shiftFactor}px, 0)`,
              transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
            };

            return (
              <div
                key={i}
                className="absolute pointer-events-auto transition-all duration-300"
                style={{
                  ...app.style,
                  ...parallaxStyle,
                }}
              >
                <div className="animate-fade-in-up animate-fill-both" style={{ animationDelay: `${500 + i * 120}ms` }}>
                  <div className={`${app.cls} orbit-icon`}>
                    <div
                      className="w-[72px] h-[72px] bg-white rounded-[1.25rem] flex items-center justify-center border transition-all duration-300"
                      style={{
                        boxShadow: `0 8px 32px ${app.glow}, 0 1px 4px rgba(0,0,0,0.03)`,
                        borderColor: 'rgba(0,0,0,0.04)',
                      }}
                    >
                      <img
                        src={app.src}
                        alt={app.name}
                        className="w-9 h-9 object-contain drop-shadow-sm transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default LandingHero;
